import React, { useState } from 'react';
import { X, FileVideo, TrendingUp, GitBranch, Bug, Shield, Gauge, AlertTriangle, CheckCircle, XCircle, Info, Target, Zap, Award, Eye, ArrowUp, ArrowDown, Minus, BarChart3, FileImage, Calendar, User, Clock, Activity, Star, ArrowRight, ArrowLeft, Percent } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { type VersionComparison } from '../lib/versions';
import { AIAnalysisService } from '../lib/ai-analysis';

interface EnhancedVersionComparisonProps {
  comparison: VersionComparison;
  onClose: () => void;
}

export default function EnhancedVersionComparison({ comparison, onClose }: EnhancedVersionComparisonProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'platforms' | 'issues'>('overview');
  const [showFullView, setShowFullView] = useState(false);
  const [fullViewVersion, setFullViewVersion] = useState<'version1' | 'version2'>('version1');
  const [overlayMode, setOverlayMode] = useState(false);
  const [showOverlayInstructions, setShowOverlayInstructions] = useState(true);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadgeColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getPerformanceGrade = (score: number) => {
    if (score >= 90) return { grade: 'A+', color: 'text-green-600' };
    if (score >= 80) return { grade: 'A', color: 'text-green-600' };
    if (score >= 70) return { grade: 'B', color: 'text-blue-600' };
    if (score >= 60) return { grade: 'C', color: 'text-yellow-600' };
    return { grade: 'D', color: 'text-red-600' };
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'info': return <Info className="w-4 h-4 text-blue-500" />;
      default: return <CheckCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const renderChangeIndicator = (oldValue: number, newValue: number, isReverse = false) => {
    const change = newValue - oldValue;
    const isPositive = isReverse ? change < 0 : change > 0;
    
    if (Math.abs(change) < 0.1) {
      return (
        <div className="flex items-center space-x-1 text-gray-500">
          <Minus className="w-3 h-3" />
          <span className="text-xs font-medium">No change</span>
        </div>
      );
    }

    return (
      <div className={`flex items-center space-x-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
        <span className="text-xs font-medium">
          {change > 0 ? '+' : ''}{change.toFixed(1)}
        </span>
      </div>
    );
  };

  const runDebugTests = async () => {
    try {
      console.log('🔍 Running debug tests for version comparison...');
      
      if (comparison?.version1?.id && comparison?.version2?.id) {
        console.log('🐛 Debugging version comparison:', {
          version1Id: comparison.version1.id,
          version2Id: comparison.version2.id,
          hasVersion1Data: !!comparison.version1,
          hasVersion2Data: !!comparison.version2,
          hasDifferences: !!comparison.differences,
          version1AnalysisData: !!comparison.version1.analysisData,
          version2AnalysisData: !!comparison.version2.analysisData
        });
        
        const analysis1 = await AIAnalysisService.getAnalysisForCreative(comparison.version1.id);
        const analysis2 = await AIAnalysisService.getAnalysisForCreative(comparison.version2.id);
        
        console.log('🧪 Direct analysis fetch results:', {
          analysis1: !!analysis1,
          analysis2: !!analysis2,
          analysis1Score: analysis1?.overallScore,
          analysis2Score: analysis2?.overallScore
        });
      }
      
      await AIAnalysisService.debugAnalysisRelationships();
      console.log('✅ Debug tests completed - check console for detailed logs');
    } catch (error) {
      console.error('❌ Debug test failed:', error);
    }
  };

  // Extract analysis data
  const version1Analysis = comparison.version1.analysisData;
  const version2Analysis = comparison.version2.analysisData;

  // Get all platforms
  const allPlatforms = new Set([
    ...(version1Analysis?.platforms ? Object.keys(version1Analysis.platforms) : []),
    ...(version2Analysis?.platforms ? Object.keys(version2Analysis.platforms) : [])
  ]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'platforms', label: 'Platforms', icon: Target },
    { id: 'issues', label: 'Issues', icon: AlertTriangle }
  ];

  const renderCreativePreview = (version: any, title: string, versionKey: 'version1' | 'version2') => {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-4 py-3 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
            <GitBranch className="w-4 h-4 text-purple-600" />
            <span>{title}</span>
            <span className="text-sm font-normal text-gray-600">
              (v{version?.versionNumber || 1})
            </span>
          </h4>
        </div>
        
        {/* Creative Preview - Now Clickable */}
        <div 
          className="aspect-video bg-gray-100 relative cursor-pointer hover:opacity-90 transition-opacity group"
          onClick={() => openFullView(versionKey)}
        >
          {version?.type === 'image' || version?.url?.includes('image') ? (
            <img
              src={version?.url || 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800'}
              alt={version?.name || 'Creative'}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <FileVideo className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Video Creative</p>
              </div>
            </div>
          )}

          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center space-x-2">
              <Eye className="w-4 h-4 text-gray-700" />
              <span className="text-sm font-medium text-gray-700">View Full Size</span>
            </div>
          </div>
          
          {/* Analysis Score Overlay */}
          {version?.analysisData && (
            <div className="absolute top-3 right-3">
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getScoreBadgeColor(version.analysisData.overallScore || 0)}`}>
                {version.analysisData.overallScore || 0}%
              </div>
            </div>
          )}
        </div>

        {/* Creative Info */}
        <div className="p-4 space-y-3">
          <div>
            <h5 className="font-medium text-gray-900 truncate">{version?.name || 'Untitled'}</h5>
            <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
              <span className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>{version?.createdAt ? new Date(version.createdAt).toLocaleDateString() : 'Unknown'}</span>
              </span>
              <span className="flex items-center space-x-1">
                <FileImage className="w-3 h-3" />
                <span>{formatFileSize(version?.fileSize)}</span>
              </span>
            </div>
          </div>

          {/* Quick Stats */}
          {version?.analysisData && (
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
              <div className="text-center">
                <div className={`text-lg font-bold ${getScoreColor(version.analysisData.overallScore || 0)}`}>
                  {version.analysisData.overallScore || 0}%
                </div>
                <div className="text-xs text-gray-600">Overall Score</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900">
                  {version.analysisData.detectedIssues?.length || 0}
                </div>
                <div className="text-xs text-gray-600">Issues</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const openFullView = (version: 'version1' | 'version2') => {
    setFullViewVersion(version);
    setShowFullView(true);
  };

  const closeFullView = () => {
    setShowFullView(false);
  };

  const switchFullViewVersion = (version: 'version1' | 'version2') => {
    setFullViewVersion(version);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-50 rounded-xl w-full max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-white border-b border-gray-200 rounded-t-xl">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
              <GitBranch className="w-6 h-6 text-white" />
            </div>
          <div>
              <h3 className="text-xl font-semibold text-gray-900">Version Comparison</h3>
              <p className="text-sm text-gray-600">
                Comparing version {comparison.version1.versionNumber || 1} with version {comparison.version2.versionNumber || 1}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
          <button
            onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white border-b border-gray-200 px-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Creative Previews Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {renderCreativePreview(comparison.version1, "Previous Version", "version1")}
                {renderCreativePreview(comparison.version2, "Current Version", "version2")}
              </div>

              {/* Change Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Overall Score Change */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <Activity className="w-4 h-4 text-blue-600" />
                      <span>Overall Score</span>
                  </h4>
                    {renderChangeIndicator(
                      version1Analysis?.overallScore || 0,
                      version2Analysis?.overallScore || 0
                    )}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {version1Analysis?.overallScore || 0}% → {version2Analysis?.overallScore || 0}%
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    Change: {((version2Analysis?.overallScore || 0) - (version1Analysis?.overallScore || 0)).toFixed(1)} points
                  </div>
                </div>
                
                {/* File Size Change */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <FileImage className="w-4 h-4 text-purple-600" />
                      <span>File Size</span>
                    </h4>
                    {comparison.version1.fileSize && comparison.version2.fileSize && 
                     renderChangeIndicator(comparison.version1.fileSize, comparison.version2.fileSize, true)}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatFileSize(comparison.version1.fileSize)} → {formatFileSize(comparison.version2.fileSize)}
                      </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {comparison.version1.fileSize && comparison.version2.fileSize ? 
                      `Change: ${(((comparison.version2.fileSize - comparison.version1.fileSize) / comparison.version1.fileSize) * 100).toFixed(1)}%` :
                      'Size comparison unavailable'
                    }
                    </div>
                </div>

                {/* Issues Change */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-orange-600" />
                      <span>Issues</span>
                    </h4>
                    {renderChangeIndicator(
                      version1Analysis?.detectedIssues?.length || 0,
                      version2Analysis?.detectedIssues?.length || 0,
                      true
                    )}
                    </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {version1Analysis?.detectedIssues?.length || 0} → {version2Analysis?.detectedIssues?.length || 0}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    Change: {((version2Analysis?.detectedIssues?.length || 0) - (version1Analysis?.detectedIssues?.length || 0))} issues
                  </div>
                </div>
              </div>

              {/* Side by Side Analysis Comparison */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Version 1 Analysis */}
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                      <ArrowLeft className="w-4 h-4 text-gray-600" />
                      <span>Previous Version Analysis</span>
                  </h4>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    {version1Analysis ? (
                      <>
                        {/* Score */}
                        <div className="text-center">
                          <div className={`text-3xl font-bold ${getScoreColor(version1Analysis.overallScore || 0)}`}>
                            {version1Analysis.overallScore || 0}%
                          </div>
                          <div className="text-sm text-gray-600">Overall Score</div>
                          <div className={`text-xs font-medium ${getPerformanceGrade(version1Analysis.overallScore || 0).color}`}>
                            Grade: {getPerformanceGrade(version1Analysis.overallScore || 0).grade}
                          </div>
                </div>
                
                        {/* Platform Breakdown */}
                        {version1Analysis.platforms && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium text-gray-700">Platform Scores</h5>
                            {Object.entries(version1Analysis.platforms).map(([platform, data]: [string, any]) => (
                              <div key={platform} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <span className="text-sm font-medium text-gray-700 capitalize">{platform}</span>
                                <div className="flex items-center space-x-2">
                                  <span className={`text-sm font-bold ${getScoreColor(data.complianceScore || 0)}`}>
                                    {data.complianceScore || 0}%
                                  </span>
                                  <span className="text-xs text-gray-600">
                                    {data.issues?.length || 0} issues
                                  </span>
                      </div>
                    </div>
                            ))}
                          </div>
                        )}

                        {/* Issues Summary */}
                        <div className="border-t border-gray-100 pt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Issues Summary</h5>
                          <div className="text-center">
                            <div className="text-xl font-bold text-red-600">
                              {version1Analysis.detectedIssues?.length || 0}
                </div>
                            <div className="text-xs text-gray-600">Total Issues</div>
                    </div>
                  </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <AlertTriangle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">No analysis data available</p>
                    </div>
                    )}
                  </div>
                </div>

                {/* Version 2 Analysis */}
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                      <ArrowRight className="w-4 h-4 text-purple-600" />
                      <span>Current Version Analysis</span>
                    </h4>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    {version2Analysis ? (
                      <>
                        {/* Score */}
                        <div className="text-center">
                          <div className={`text-3xl font-bold ${getScoreColor(version2Analysis.overallScore || 0)}`}>
                            {version2Analysis.overallScore || 0}%
                          </div>
                          <div className="text-sm text-gray-600">Overall Score</div>
                          <div className={`text-xs font-medium ${getPerformanceGrade(version2Analysis.overallScore || 0).color}`}>
                            Grade: {getPerformanceGrade(version2Analysis.overallScore || 0).grade}
              </div>
            </div>

                        {/* Platform Breakdown */}
                        {version2Analysis.platforms && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium text-gray-700">Platform Scores</h5>
                            {Object.entries(version2Analysis.platforms).map(([platform, data]: [string, any]) => (
                              <div key={platform} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <span className="text-sm font-medium text-gray-700 capitalize">{platform}</span>
                                <div className="flex items-center space-x-2">
                                  <span className={`text-sm font-bold ${getScoreColor(data.complianceScore || 0)}`}>
                                    {data.complianceScore || 0}%
                                  </span>
                                  <span className="text-xs text-gray-600">
                                    {data.issues?.length || 0} issues
                                  </span>
                    </div>
                  </div>
                            ))}
                          </div>
                        )}

                        {/* Issues Summary */}
                        <div className="border-t border-gray-100 pt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Issues Summary</h5>
                  <div className="text-center">
                            <div className="text-xl font-bold text-red-600">
                              {version2Analysis.detectedIssues?.length || 0}
                    </div>
                            <div className="text-xs text-gray-600">Total Issues</div>
                    </div>
                  </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <AlertTriangle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">No analysis data available</p>
                    </div>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          {activeTab === 'platforms' && (
            <div className="space-y-6">
              {Array.from(allPlatforms).length > 0 ? (
                Array.from(allPlatforms).map((platform) => {
                  const platform1Data = version1Analysis?.platforms?.[platform];
                  const platform2Data = version2Analysis?.platforms?.[platform];
                  
                  return (
                    <div key={platform} className="bg-white rounded-xl border border-gray-200">
                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-900 capitalize flex items-center space-x-2">
                          <Target className="w-4 h-4 text-purple-600" />
                          <span>{platform} Comparison</span>
                        </h4>
                      </div>
                      
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Previous Version */}
              <div className="space-y-4">
                            <h5 className="text-sm font-medium text-gray-700 border-b border-gray-100 pb-2">
                              Previous Version
                            </h5>
                            {platform1Data ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">Compliance Score</span>
                                  <span className={`font-bold ${getScoreColor(platform1Data.complianceScore || 0)}`}>
                                    {platform1Data.complianceScore || 0}%
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">Performance Score</span>
                                  <span className={`font-bold ${getScoreColor(platform1Data.performanceScore || 0)}`}>
                                    {platform1Data.performanceScore || 0}%
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">Issues</span>
                                  <span className="font-bold text-red-600">
                                    {platform1Data.issues?.length || 0}
                            </span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">No data available</p>
                            )}
                          </div>

                          {/* Current Version */}
                          <div className="space-y-4">
                            <h5 className="text-sm font-medium text-gray-700 border-b border-gray-100 pb-2">
                              Current Version
                            </h5>
                            {platform2Data ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">Compliance Score</span>
                                  <div className="flex items-center space-x-2">
                                    <span className={`font-bold ${getScoreColor(platform2Data.complianceScore || 0)}`}>
                                      {platform2Data.complianceScore || 0}%
                                    </span>
                                    {platform1Data && renderChangeIndicator(
                                      platform1Data.complianceScore || 0,
                                      platform2Data.complianceScore || 0
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">Performance Score</span>
                                  <div className="flex items-center space-x-2">
                                    <span className={`font-bold ${getScoreColor(platform2Data.performanceScore || 0)}`}>
                                      {platform2Data.performanceScore || 0}%
                                    </span>
                                    {platform1Data && renderChangeIndicator(
                                      platform1Data.performanceScore || 0,
                                      platform2Data.performanceScore || 0
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">Issues</span>
                                  <div className="flex items-center space-x-2">
                                    <span className="font-bold text-red-600">
                                      {platform2Data.issues?.length || 0}
                                    </span>
                                    {platform1Data && renderChangeIndicator(
                                      platform1Data.issues?.length || 0,
                                      platform2Data.issues?.length || 0,
                                      true
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">No data available</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                  <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Platform Data</h3>
                  <p className="text-gray-600">No platform analysis data available for comparison.</p>
                </div>
              )}
                  </div>
                )}

          {activeTab === 'issues' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Previous Version Issues */}
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-gray-600" />
                      <span>Previous Version Issues</span>
                      <span className="text-sm font-normal text-gray-600">
                        ({version1Analysis?.detectedIssues?.length || 0})
                      </span>
                    </h4>
                  </div>
                  
                  <div className="p-6">
                    {version1Analysis?.detectedIssues && version1Analysis.detectedIssues.length > 0 ? (
                      <div className="space-y-3">
                        {version1Analysis.detectedIssues.map((issue: any, index: number) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              {getSeverityIcon(issue.severity)}
                              <div className="flex-1 min-w-0">
                                <h5 className="text-sm font-medium text-gray-900">{issue.title}</h5>
                                <p className="text-xs text-gray-600 mt-1">{issue.description}</p>
                                <div className="flex items-center space-x-2 mt-2">
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    issue.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                    issue.severity === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-blue-100 text-blue-800'
                                  }`}>
                                    {issue.severity}
                                  </span>
                                  {issue.platform && (
                                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded-full capitalize">
                                      {issue.platform}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            </div>
                          ))}
                        </div>
                    ) : (
                      <div className="text-center py-8">
                        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">No issues detected</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Current Version Issues */}
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-purple-600" />
                      <span>Current Version Issues</span>
                      <span className="text-sm font-normal text-gray-600">
                        ({version2Analysis?.detectedIssues?.length || 0})
                      </span>
                    </h4>
                  </div>
                  
                  <div className="p-6">
                    {version2Analysis?.detectedIssues && version2Analysis.detectedIssues.length > 0 ? (
                      <div className="space-y-3">
                        {version2Analysis.detectedIssues.map((issue: any, index: number) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              {getSeverityIcon(issue.severity)}
                              <div className="flex-1 min-w-0">
                                <h5 className="text-sm font-medium text-gray-900">{issue.title}</h5>
                                <p className="text-xs text-gray-600 mt-1">{issue.description}</p>
                                <div className="flex items-center space-x-2 mt-2">
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    issue.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                    issue.severity === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-blue-100 text-blue-800'
                                  }`}>
                                    {issue.severity}
                                  </span>
                                  {issue.platform && (
                                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded-full capitalize">
                                      {issue.platform}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            </div>
                          ))}
                        </div>
                    ) : (
                      <div className="text-center py-8">
                        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">No issues detected</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full View Modal */}
      {showFullView && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4" onClick={closeFullView}>
          <div className="relative max-w-[95vw] max-h-[95vh] flex flex-col w-full h-full" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-white px-4 py-3 flex items-center justify-between rounded-t-lg">
              <div className="flex items-center space-x-3">
                <h4 className="font-semibold text-gray-900">
                  {overlayMode ? 'Overlay Comparison Mode' : (fullViewVersion === 'version1' ? 'Previous Version' : 'Current Version')}
                </h4>
                <span className="text-sm text-gray-600">
                  {overlayMode 
                    ? `v${comparison.version1.versionNumber || 1} + v${comparison.version2.versionNumber || 1}` 
                    : `v${fullViewVersion === 'version1' ? (comparison.version1.versionNumber || 1) : (comparison.version2.versionNumber || 1)}`
                  }
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                {/* Overlay Mode Toggle */}
                <button
                  onClick={() => {
                    setOverlayMode(!overlayMode);
                    // Show instructions when entering overlay mode
                    if (!overlayMode) {
                      setShowOverlayInstructions(true);
                    }
                  }}
                  className={`flex items-center space-x-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    overlayMode
                      ? 'bg-purple-100 text-purple-700 border border-purple-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Toggle overlay mode to spot differences"
                >
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-gray-400 rounded-sm opacity-50"></div>
                    <div className="w-3 h-3 bg-gray-600 rounded-sm -ml-1"></div>
                  </div>
                  <span>{overlayMode ? 'Overlay ON' : 'Overlay Mode'}</span>
                </button>

                {/* Version Switch Buttons - Only show when not in overlay mode */}
                {!overlayMode && (
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => switchFullViewVersion('version1')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        fullViewVersion === 'version1'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Previous (v{comparison.version1.versionNumber || 1})
                    </button>
                    <button
                      onClick={() => switchFullViewVersion('version2')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        fullViewVersion === 'version2'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Current (v{comparison.version2.versionNumber || 1})
                    </button>
                  </div>
                )}
                
                <button
                  onClick={closeFullView}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="bg-black flex items-center justify-center min-h-[60vh] relative flex-1">
              {overlayMode ? (
                /* Overlay Mode - Show both versions with proper positioning and blending */
                <div className="relative w-full h-full flex items-center justify-center p-4 bg-gray-900">
                  {/* Image container with fixed dimensions to prevent overflow */}
                  <div className="relative max-w-[90%] max-h-[70vh] mx-auto">
                    {/* First version (bottom layer) - this sets the container size */}
                    {(() => {
                      const version1 = comparison.version1;
                      if (version1?.type === 'image' || version1?.url?.includes('image')) {
                        return (
                          <img
                            src={version1.url || 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800'}
                            alt={version1.name || 'Previous Version'}
                            className="block w-full h-auto max-h-[70vh] object-contain overlay-image rounded-lg shadow-2xl"
                            style={{ 
                              filter: 'hue-rotate(200deg) saturate(1.2) brightness(1.2)',
                              mixBlendMode: 'soft-light',
                              opacity: 0.7
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800';
                            }}
                          />
                        );
                      } else if (version1?.type === 'video') {
                        return (
                          <video
                            className="block w-full h-auto max-h-[70vh] object-contain overlay-image rounded-lg shadow-2xl"
                            style={{ 
                              filter: 'hue-rotate(200deg) saturate(1.2) brightness(1.2)',
                              mixBlendMode: 'soft-light',
                              opacity: 0.7
                            }}
                            muted
                            loop
                            autoPlay
                          >
                            <source src={version1.url} type="video/mp4" />
                          </video>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Second version (top layer) - positioned absolutely to match first image exactly */}
                    {(() => {
                      const version2 = comparison.version2;
                      if (version2?.type === 'image' || version2?.url?.includes('image')) {
                        return (
                          <img
                            src={version2.url || 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800'}
                            alt={version2.name || 'Current Version'}
                            className="absolute top-0 left-0 w-full h-full object-contain overlay-image rounded-lg"
                            style={{ 
                              filter: 'hue-rotate(40deg) saturate(1.2) brightness(1.2)',
                              mixBlendMode: 'lighten',
                              opacity: 0.7
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800';
                            }}
                          />
                        );
                      } else if (version2?.type === 'video') {
                        return (
                          <video
                            className="absolute top-0 left-0 w-full h-full object-contain overlay-image rounded-lg"
                            style={{ 
                              filter: 'hue-rotate(40deg) saturate(1.2) brightness(1.2)',
                              mixBlendMode: 'lighten',
                              opacity: 0.7
                            }}
                            muted
                            loop
                            autoPlay
                          >
                            <source src={version2.url} type="video/mp4" />
                          </video>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  
                  {/* Enhanced Overlay Instructions - Positioned at bottom center */}
                  {showOverlayInstructions && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-90 text-white px-4 py-2 rounded-lg backdrop-blur-sm border border-gray-600 z-30 max-w-xl">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3 text-xs">
                          <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 bg-blue-500 rounded-sm opacity-80"></div>
                            <span>Previous v{(comparison.version1.versionNumber && comparison.version1.versionNumber < 1000) ? comparison.version1.versionNumber : 1}</span>
                          </div>
                          <span className="text-gray-300">+</span>
                          <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 bg-orange-500 rounded-sm opacity-80"></div>
                            <span>Current v{(comparison.version2.versionNumber && comparison.version2.versionNumber < 1000) ? comparison.version2.versionNumber : 2}</span>
                          </div>
                          <span className="text-gray-300">=</span>
                          <div className="flex items-center space-x-1">
                            <Eye className="w-3 h-3" />
                            <span className="text-green-300">Differences</span>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowOverlayInstructions(false)}
                          className="ml-2 p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                          title="Hide instructions"
                        >
                          <X className="w-3 h-3 text-gray-300 hover:text-white" />
                        </button>
                      </div>
                      <div className="text-xs text-gray-300 text-center">
                        Blue tint = Previous • Orange tint = Current • Overlapping areas show differences
                      </div>
                    </div>
                  )}

                  {/* Toggle Controls for Blend Modes - Positioned outside image area */}
                  <div className="absolute top-4 right-4 bg-black bg-opacity-90 rounded-lg p-3 backdrop-blur-sm border border-gray-600 z-30">
                    <div className="text-xs text-white mb-2 font-medium">Controls:</div>
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => {
                          // Toggle between different blend modes for better difference detection
                          const images = document.querySelectorAll('.overlay-image');
                          images.forEach((img, index) => {
                            const element = img as HTMLElement;
                            if (index === 0) {
                              // Bottom layer (version1)
                              const currentBlend = element.style.mixBlendMode;
                              element.style.mixBlendMode = currentBlend === 'soft-light' ? 'multiply' : 
                                                         currentBlend === 'multiply' ? 'normal' : 'soft-light';
                            } else {
                              // Top layer (version2)
                              const currentBlend = element.style.mixBlendMode;
                              element.style.mixBlendMode = currentBlend === 'lighten' ? 'overlay' : 
                                                         currentBlend === 'overlay' ? 'difference' : 'lighten';
                            }
                          });
                        }}
                        className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                      >
                        Change Blend
                      </button>
                      <button
                        onClick={() => {
                          // Toggle opacity for better visibility
                          const images = document.querySelectorAll('.overlay-image');
                          images.forEach((img) => {
                            const element = img as HTMLElement;
                            const currentOpacity = parseFloat(element.style.opacity);
                            element.style.opacity = currentOpacity === 0.7 ? '0.5' : 
                                                   currentOpacity === 0.5 ? '0.9' : '0.7';
                          });
                        }}
                        className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                      >
                        Opacity
                      </button>
                      <button
                        onClick={() => {
                          // Reset to default blend modes and settings
                          const images = document.querySelectorAll('.overlay-image');
                          images.forEach((img, index) => {
                            const element = img as HTMLElement;
                            if (index === 0) {
                              element.style.mixBlendMode = 'soft-light';
                              element.style.opacity = '0.7';
                            } else {
                              element.style.mixBlendMode = 'lighten';
                              element.style.opacity = '0.7';
                            }
                          });
                        }}
                        className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                      >
                        Reset
                      </button>
                      <button
                        onClick={() => setShowOverlayInstructions(!showOverlayInstructions)}
                        className="text-xs px-2 py-1 bg-blue-700 hover:bg-blue-600 text-white rounded transition-colors"
                      >
                        {showOverlayInstructions ? 'Hide Guide' : 'Show Guide'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Normal Mode - Show single version */
                (() => {
                  const currentVersion = fullViewVersion === 'version1' ? comparison.version1 : comparison.version2;
                  
                  if (currentVersion?.type === 'image' || currentVersion?.url?.includes('image')) {
                    return (
                      <img
                        src={currentVersion.url || 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800'}
                        alt={currentVersion.name || 'Creative'}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800';
                        }}
                      />
                    );
                   } else if (currentVersion?.type === 'video') {
                     return (
                       <video
                         controls
                         className="max-w-full max-h-full"
                       >
                         <source src={currentVersion.url} type="video/mp4" />
                         Your browser does not support the video tag.
                       </video>
                     );
                   } else {
                    return (
                      <div className="text-center text-white p-8">
                        <FileImage className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-lg">No preview available</p>
                        <p className="text-sm opacity-75">Creative could not be displayed</p>
                      </div>
                    );
                  }
                })()
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-white px-4 py-3 rounded-b-lg z-40 relative">
              {overlayMode ? (
                /* Overlay Mode Footer - Show both versions */
                <div className="space-y-2">
                  <div className="text-xs text-center text-gray-500 mb-2">
                    Both versions overlaid at 50% opacity with color tinting for easier differentiation
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {/* Previous Version Info */}
                    <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-400 rounded-sm opacity-75"></div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {comparison.version1.name || 'Previous Version'}
                          </div>
                          <div className="text-xs text-gray-600">
                            v{comparison.version1.versionNumber || 1} • {formatFileSize(comparison.version1.fileSize)}
                          </div>
                        </div>
                      </div>
                      {comparison.version1.analysisData && (
                        <div className="text-right">
                          <div className={`text-xs px-2 py-1 rounded-full ${getScoreBadgeColor(comparison.version1.analysisData.overallScore || 0)}`}>
                            {comparison.version1.analysisData.overallScore || 0}%
                    </div>
                  </div>
                )}
                    </div>

                    {/* Current Version Info */}
                    <div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-purple-400 rounded-sm opacity-75"></div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {comparison.version2.name || 'Current Version'}
                          </div>
                          <div className="text-xs text-gray-600">
                            v{comparison.version2.versionNumber || 1} • {formatFileSize(comparison.version2.fileSize)}
                          </div>
                      </div>
                      </div>
                      {comparison.version2.analysisData && (
                        <div className="text-right">
                          <div className={`text-xs px-2 py-1 rounded-full ${getScoreBadgeColor(comparison.version2.analysisData.overallScore || 0)}`}>
                            {comparison.version2.analysisData.overallScore || 0}%
                    </div>
                  </div>
                )}
              </div>
                  </div>
                </div>
              ) : (
                /* Normal Mode Footer - Show current version */
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center space-x-4">
                    <span>
                      <strong>{fullViewVersion === 'version1' ? comparison.version1.name : comparison.version2.name || 'Untitled'}</strong>
                    </span>
                    <span>
                      {formatFileSize(fullViewVersion === 'version1' ? comparison.version1.fileSize : comparison.version2.fileSize)}
                    </span>
                    <span>
                      {fullViewVersion === 'version1' 
                        ? (comparison.version1.createdAt ? new Date(comparison.version1.createdAt).toLocaleDateString() : 'Unknown date')
                        : (comparison.version2.createdAt ? new Date(comparison.version2.createdAt).toLocaleDateString() : 'Unknown date')
                      }
                    </span>
                  </div>
                  
                  {(() => {
                    const currentAnalysis = fullViewVersion === 'version1' ? comparison.version1.analysisData : comparison.version2.analysisData;
                    return currentAnalysis ? (
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getScoreBadgeColor(currentAnalysis.overallScore || 0)}`}>
                          Score: {currentAnalysis.overallScore || 0}%
                        </span>
                        <span className="text-xs">
                          {currentAnalysis.detectedIssues?.length || 0} issues
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">No analysis available</span>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 